/**
 * DeskFindingRowMolecule — one row of the Desk's "Needs attention" list: a finding's severity,
 * message, owning template and expandable `PublishFinding[]` detail.
 *
 * Pre-create check (atomic-design Rule 3): `FactorListItemAtom` and `AlertCardMolecule` were
 * considered and rejected — the former is bound to status factors, the latter is a dismissible
 * page-level alert, and neither carries the expand-detail + navigate-to-target affordance a
 * finding needs. Severity rendering reuses the canonical `StatusBadgeAtom` (via `SEVERITY_TONE`)
 * and the action reuses `ButtonAtom`; only the row composition is new.
 */

import React, { useState } from 'react';
import { useTranslation } from 'localization';
import { PrimeReactButton as ButtonAtom, StatusBadgeAtom } from 'design-system/my-lib';
import { SEVERITY_TONE } from 'design-system/my-lib';
import type { DeskFinding } from 'types/studio';
import { studioRouteForTemplate } from './studioRouteForTemplate';
import './DeskFindingRowMolecule.scss';

export interface DeskFindingRowMoleculeProps {
  finding: DeskFinding;
  onOpen: (finding: DeskFinding, route: string) => void;
  className?: string;
}

/**
 * A single Needs-attention row (spec UI Components table): severity stripe, message, template
 * ref, "Open" (navigates to `target.route`, `?node=` appended when the finding pinpoints a
 * node), and an expandable `detail` list for `publish_blocked` findings.
 */
const DeskFindingRowMolecule: React.FC<DeskFindingRowMoleculeProps> = ({ finding, onOpen, className = '' }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const baseRoute = studioRouteForTemplate(finding.template) ?? finding.target.route;
  const route = finding.target.nodeId
    ? `${baseRoute}?node=${encodeURIComponent(finding.target.nodeId)}`
    : baseRoute;

  return (
    <li
      className={['desk-finding-row', `desk-finding-row--${finding.severity}`, className].filter(Boolean).join(' ')}
      data-testid={`desk-finding-row-${finding.id}`}
    >
      <div className="desk-finding-row__main">
        <StatusBadgeAtom
          tone={SEVERITY_TONE[finding.severity]}
          label={t(`studio.desk.findings.kind.${finding.kind}`)}
          size="small"
        />
        <span className="desk-finding-row__message">{finding.message}</span>
        <span className="desk-finding-row__template">{finding.template.displayName}</span>
        <ButtonAtom
          variant="text"
          size="small"
          data-testid={`desk-finding-open-${finding.id}`}
          onClick={() => onOpen(finding, route)}
        >
          {t('studio.desk.findings.open')}
        </ButtonAtom>
        {finding.detail && finding.detail.length > 0 && (
          <ButtonAtom
            variant="text"
            size="small"
            data-testid={`desk-finding-toggle-${finding.id}`}
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded
              ? t('studio.desk.findings.collapse')
              : t('studio.desk.findings.expand', { count: finding.detail.length })}
          </ButtonAtom>
        )}
      </div>
      {expanded && finding.detail && finding.detail.length > 0 && (
        <ul className="desk-finding-row__detail" data-testid={`desk-finding-detail-${finding.id}`}>
          {finding.detail.map((entry, index) => (
            <li key={`${entry.code}-${index}`}>{entry.message}</li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default DeskFindingRowMolecule;
